import { Component, OnInit } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms'
// import { ProgressStepsComponent, StepDef } from './progress-steps/progress-steps.component'
import { Router } from '@angular/router'
import { nicknameAsyncValidator } from './validators/nickname-async.validator'
import { kanaToRomaji } from '../../utils/kana-to-romaji'
import { ApiCatalogService } from '../../api/api-catalog.service'
import { AccountRegistrationService } from '../../services/account-registration.service'
import { MessageService } from '../../components/message/message.service'
import { BizProgressStepsComponent, StepDef } from '@interserve-corp/angular'
function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const password = control.parent?.get('password')?.value
  return password === control.value ? null : { mismatch: true }
}

const NICKNAME_MAX_LENGTH = 30
const PASSWORD_PATTERN = /^[A-Za-z0-9!"#$%'()*,.\/:;<=>?@\[\]\^_`{|}~]+$/

function noSpaceValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value as string | null
  if (!value) return null

  return /\s/.test(value) ? { noSpace: true } : null
}

type ErrorMessage = string | ((errorValue: any) => string)

// フィールド1件の定義型
export interface FieldDef {
  controlName: string
  type: 'text' | 'password' | 'email'
  placeholder: string
  errorMessages?: {
    key: string
    message: ErrorMessage
  }[]
  pendingMessage?: string
}

// 名前フィールド（横並び）のグループ定義型
export interface FieldRowDef {
  isRow: true
  fields: FieldDef[]
}

// ユニオン型（単体 or 横並びグループ）
export type FieldItem = FieldDef | FieldRowDef

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, BizProgressStepsComponent],
})
export class RegistrationComponent implements OnInit {
  form!: FormGroup
  activeField: string | null = null
  iconUrl: string = 'assets/img/icon_biz-marche_c.png'
  showUserTos = false

  // フィールド定義配列（表示順に定義）
  readonly fieldItems: FieldItem[] = [
    {
      controlName: 'nickname',
      type: 'text',
      placeholder: 'ニックネーム',
      pendingMessage: '確認中...',
      errorMessages: [
        { key: 'required', message: 'ニックネームを入力してください' },
        { key: 'noSpace', message: 'スペースは入力できません' },
        {
          key: 'maxlength',
          message: (e) => `${e.requiredLength}文字以内で入力してください`,
        },
        { key: 'nicknameTaken', message: 'このIDはすでに使われています' },
      ],
    },
    {
      controlName: 'password',
      type: 'password',
      placeholder: 'パスワード入力',
      errorMessages: [
        { key: 'required', message: 'パスワードを入力してください' },
        { key: 'noSpace', message: 'スペースは入力できません' },
        {
          key: 'minlength',
          message: (e) => `${e.requiredLength}文字以上で入力してください`,
        },
        {
          key: 'pattern',
          message: `半角英数字記号のみ使用できます（利用可能な記号: ! " # $ % ' ( ) * , . / : ; < = > ? @ [ ] ^ _ \` { | } ~）`,
        },
      ],
    },
    {
      controlName: 'passwordConfirm',
      type: 'password',
      placeholder: 'パスワード再入力',
      errorMessages: [
        { key: 'required', message: '確認用パスワードを入力してください' },
        { key: 'mismatch', message: 'パスワードが一致しません' },
      ],
    },
    {
      isRow: true,
      fields: [
        {
          controlName: 'familyName',
          type: 'text',
          placeholder: '苗字',
          errorMessages: [{ key: 'required', message: '苗字を入力してください' }],
        },
        {
          controlName: 'firstName',
          type: 'text',
          placeholder: '名前',
          errorMessages: [{ key: 'required', message: '名前を入力してください' }],
        },
      ],
    },
    {
      isRow: true,
      fields: [
        {
          controlName: 'familyNameKana',
          type: 'text',
          placeholder: 'みょうじ',
          errorMessages: [{ key: 'required', message: 'みょうじを入力してください' }],
        },
        {
          controlName: 'firstNameKana',
          type: 'text',
          placeholder: 'なまえ',
          errorMessages: [{ key: 'required', message: 'なまえを入力してください' }],
        },
      ],
    },
    {
      controlName: 'email',
      type: 'email',
      placeholder: 'bizmaru@bizmarche.com',
      errorMessages: [
        { key: 'required', message: 'メールアドレスを入力してください' },
        { key: 'email', message: 'メールアドレスの形式が正しくありません' },
      ],
    },
  ]

  readonly steps: StepDef[] = [
    { label: 'ニックネーム入力', fields: ['nickname'] },
    { label: 'パスワード入力', fields: ['password'] },
    { label: 'パスワード確認', fields: ['passwordConfirm'] },
    { label: 'お名前入力', fields: ['familyName', 'firstName'] },
    { label: 'お名前（かな）入力', fields: ['familyNameKana', 'firstNameKana'] },
    { label: 'メールアドレス入力', fields: ['email'] },
  ]

  // アカウントタイプ（管理者/一般）はログイン後の初期設定で決めるため、この画面では扱わない
  readonly buttonLabel = '利用規約の確認に進む'
  sending = false
  constructor(
    private fb: FormBuilder,
    private apiCatalogService: ApiCatalogService,
    private router: Router,
    private accountRegistrationService: AccountRegistrationService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      nickname: this.fb.control('', {
        validators: [Validators.required, noSpaceValidator, Validators.maxLength(NICKNAME_MAX_LENGTH)],
        asyncValidators: [nicknameAsyncValidator(this.apiCatalogService)],
        updateOn: 'blur',
      }),
      password: [
        '',
        [Validators.required, noSpaceValidator, Validators.minLength(8), Validators.pattern(PASSWORD_PATTERN)],
      ],
      passwordConfirm: ['', [Validators.required, passwordMatchValidator]],
      email: ['', [Validators.required, Validators.email]],
      familyName: ['', [Validators.required]],
      firstName: ['', [Validators.required]],
      familyNameKana: ['', [Validators.required]],
      firstNameKana: ['', [Validators.required]],
    })

    this.form.get('password')!.valueChanges.subscribe(() => {
      this.form.get('passwordConfirm')!.updateValueAndValidity()
    })
  }

  // 型ガード（横並びグループかどうかを判定）
  isRow(item: FieldItem): item is FieldRowDef {
    return (item as FieldRowDef).isRow === true
  }
  //  テンプレートで型安全に FieldDef へキャストするヘルパー
  asField(item: FieldItem): FieldDef {
    return item as FieldDef
  }
  get isCheckingNickname(): boolean {
    return this.form.get('nickname')!.pending
  }
  private getAllFields(): FieldDef[] {
    return this.fieldItems.flatMap((item) => (this.isRow(item) ? item.fields : [item]))
  }

  private findField(controlName: string): FieldDef | undefined {
    return this.getAllFields().find((field) => field.controlName === controlName)
  }
  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (!control || !control.touched || !control.errors) return null

    const field = this.findField(controlName)
    if (!field?.errorMessages?.length) return null

    for (const def of field.errorMessages) {
      if (control.hasError(def.key)) {
        const errorValue = control.getError(def.key)
        return typeof def.message === 'function' ? def.message(errorValue) : def.message
      }
    }

    return null
  }
  isChecking(controlName: string): boolean {
    return !!this.form.get(controlName)?.pending
  }

  getPendingMessage(controlName: string): string | null {
    const field = this.findField(controlName)
    return field?.pendingMessage ?? null
  }

  hasDisplayError(controlName: string): boolean {
    const control = this.form.get(controlName)
    return !!control && control.invalid && control.touched
  }

  // ブラウザの入力候補（オートコンプリート）を抑止する値を返す
  getAutocomplete(controlName: string): string {
    const field = this.findField(controlName)
    if (field?.type === 'password') return 'new-password'
    return 'off'
  }

  onFocus(field: string): void {
    this.activeField = field
  }
  onBlur(): void {
    this.activeField = null
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return // バリデーションエラーがあればここで終了
    }
    const value = this.form.getRawValue()
    this.accountRegistrationService.setRegistrationData({
      ...value,
      // 入力欄は email だが、アカウント登録 API のモデルは mailAddress なので詰め替える
      mailAddress: value.email,
      // かな入力からローマ字（パスポート式ヘボン）に変換した表記も一緒に登録する
      familyNameHepburn: kanaToRomaji(value.familyNameKana),
      firstNameHepburn: kanaToRomaji(value.firstNameKana),
    })
    // 会社情報の登録・管理者権限の付与はログイン後の初期設定（dashboard.ui）で行うため、
    // ここでは会社検索へ寄らず、そのまま利用規約同意 → アカウント登録へ進む
    this.router.navigate(['/registration-confirm'])
  }
}
